import React, { useState, useRef, useEffect } from 'react';
import appLogo from '../assets/Apps.svg';
import stackLogo from '../assets/stack.svg';
import { handleActivationKey, handleEscapeKey } from '../utils/a11y.utils';
import useListboxDropdown from '../hooks/useListboxDropdown';

const EntityDropdown = ({ entityList, selectedEntity, onSelectEntity, customDetails, labelId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const otherEntities =
    entityList?.filter((entity) => entity.name !== selectedEntity?.name) ?? [];

  const {
    listboxId,
    toggleId,
    close,
    open,
    handleToggleKeyDown,
    handleOptionKeyDown,
    setOptionRef,
  } = useListboxDropdown({
    isOpen,
    setIsOpen,
    optionCount: otherEntities.length,
    onSelectIndex: (index) => handleSelectEntity(otherEntities[index]),
    label: 'App list',
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleDocumentKeyDown = (event) => {
      handleEscapeKey(event, () => close());
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [close]);

  const toggleDropdown = () => {
    if (isOpen) close();
    else open();
  };

  const handleSelectEntity = (entity) => {
    onSelectEntity(entity);
    close();
  };

  const renderEntityOption = (entity, { isToggle = false, optionIndex = null } = {}) => {
    const label = `${entity.name}${entity.count !== undefined ? `, ${entity.count} consents` : ''}`;

    return (
      <button
        type="button"
        key={entity.name}
        id={isToggle ? toggleId : undefined}
        ref={optionIndex !== null ? (node) => setOptionRef(optionIndex, node) : undefined}
        role={isToggle ? undefined : 'option'}
        aria-selected={isToggle ? undefined : false}
        aria-label={label}
        className={`d-flex align-items-center p-2 w-100 card-action-btn ${!isToggle ? 'entity-option' : ''}`}
        style={{
          cursor: 'pointer',
          borderBottom: !isToggle ? '1px solid #f0f0f0' : 'none',
          backgroundColor: isToggle ? 'transparent' : 'white',
          minHeight: '40px',
          borderRadius: isToggle ? '20px' : 0,
        }}
        aria-expanded={isToggle && entityList.length > 1 ? isOpen : undefined}
        aria-haspopup={isToggle && entityList.length > 1 ? 'listbox' : undefined}
        aria-controls={isToggle && entityList.length > 1 ? listboxId : undefined}
        aria-labelledby={isToggle && labelId ? labelId : undefined}
        disabled={isToggle && entityList.length <= 1}
        onClick={() => {
          if (isToggle) toggleDropdown();
          else handleSelectEntity(entity);
        }}
        onKeyDown={(event) => {
          if (isToggle) {
            handleToggleKeyDown(event);
            handleActivationKey(event, toggleDropdown);
            return;
          }
          if (optionIndex !== null) {
            handleOptionKeyDown(event, optionIndex);
          }
        }}
      >
        <div className="flex-shrink-0" style={{ width: '24px', height: '24px', marginLeft:'5px' ,marginBottom:'2.5px', marginRight: '8px'}}>
          <img
            src={entity.name === 'All' ? stackLogo : (entity.entityLogoUri || appLogo)}
            alt=""
            aria-hidden="true"
            className="w-100 h-100 rounded"
            style={{ objectFit: 'contain' }}
            onError={(e) => {
              e.target.style.display = 'none';
              if (e.target.nextSibling) {
                e.target.nextSibling.style.display = 'flex';
              }
            }}
          />
        </div>

        <div className="flex-grow-1">
          <div style={{ fontSize: '12px', color: customDetails?.primaryBtnColor, textAlign: 'left' }}>
            <span className="fw-600">{entity.name}</span>
            {entity.count !== undefined && (
              <span className="ms-2" style={{ color: '#6B6D6F' }}>
                ({entity.count})
              </span>
            )}
          </div>
        </div>

        {isToggle && entityList.length > 1 && (
          <div className="ms-2" aria-hidden="true">
            <div
              style={{
                width: '12px',
                height: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            >
              <div
                style={{
                  width: '4px',
                  height: '4px',
                  borderRight: `1px solid ${customDetails?.primaryBtnColor}`,
                  borderBottom: `1px solid ${customDetails?.primaryBtnColor}`,
                  transform: 'rotate(45deg)',
                  marginTop: '-1px',
                }}
              />
            </div>
          </div>
        )}
      </button>
    );
  };

  if (!entityList || entityList.length === 0) {
    return null;
  }

  const currentEntity = entityList.length === 1 ? entityList[0] : selectedEntity;

  return (
    <div
      className="mb-3 mb-md-0 position-relative entity-dropdown-container"
      ref={dropdownRef}
      style={{
        backgroundColor: "#FFFFFF",
        borderRadius: '20px',
        border: '2px solid #D9D9D9',
      }}
    >
      {renderEntityOption(currentEntity, { isToggle: true })}

      {entityList.length > 1 && isOpen && (
        <div
          id={listboxId}
          role="listbox"
          aria-label="Select app"
          className="position-absolute w-100 bg-white border rounded shadow mt-1"
          style={{
            top: '100%',
            left: '0',
            zIndex: 1000,
            maxHeight: '300px',
            overflowY: 'auto',
          }}
        >
          {otherEntities.map((entity, index) =>
            renderEntityOption(entity, { optionIndex: index })
          )}
        </div>
      )}

      <style jsx>{`
        .entity-option:hover {
          background-color: var(--bs-light) !important;
        }
        .entity-dropdown-container {
          flex: 1;
          min-width: 200px;
          border-radius: 3rem;
        }
        @media (max-width: 600px) {
          .entity-dropdown-container {
            flex: 1 1 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default EntityDropdown;
